/**
 * The seating dialog's arithmetic, and the invite link that brings people to it.
 *
 * Kept apart from the component so it can be tested without a DOM: which
 * seats a player may take, how seats group into teams, which colors are free.
 */

import type { LobbySeat, TableLobbyMessage, TeamColor } from "../server/protocol.js";
import { TEAM_COLOR_ORDER } from "./palette.js";

/** The query parameter an invite link carries the table id in. */
export const TABLE_PARAM = "table";

/**
 * The shape the server's table ids take. The wire contract is type-only, so
 * this restates `TABLE_ID_PATTERN` in the server's messages module. The server
 * drops a malformed id as an unrecognized frame rather than answering it, so
 * the client turns a mangled link away itself.
 */
const TABLE_ID_PATTERN = /^[A-Za-z0-9_-]{12}$/;

/**
 * Who is playing a seat: a person, a bot, or a bot covering for a person
 * whose connection dropped.
 */
export type SeatHolder = "away" | "bot" | "player";

/** Something that happened to a seat mid-game that the table should point out. */
export type SeatChangeKind = "away" | "joined" | "left" | "returned";

export interface SeatChange {
    readonly playerId: string;
    readonly kind: SeatChangeKind;
    /** The person it happened to; for a departure, the name the seat no longer shows. */
    readonly name: string;
}

export interface SeatChangeText {
    /** A few words beside the seat itself. */
    readonly callout: string;
    /** The whole story, for a screen reader. */
    readonly announcement: string;
}

export interface LobbyTeam {
    readonly teamId: string;
    readonly color: TeamColor;
    /** In seat order, which puts partners next to each other in the list. */
    readonly seats: readonly LobbySeat[];
}

/**
 * Whatever an invite link carries, or null when the page was not opened from
 * one. It is returned unchecked: a link mangled in copying is still an invite
 * the player followed, and they should hear that it leads nowhere.
 */
export function readInvite(search: string): string | null {
    return new URLSearchParams(search).get(TABLE_PARAM);
}

/** True when an invite could name a table the server handed out. */
export function isTableId(tableId: string): boolean {
    return TABLE_ID_PATTERN.test(tableId);
}

export function buildInviteUrl(location: Pick<Location, "origin" | "pathname">, tableId: string): string {
    const url = new URL(location.pathname, location.origin);

    url.searchParams.set(TABLE_PARAM, tableId);

    return url.href;
}

/** The page's address without its invite, so a reload resumes rather than joining again. */
export function removeInvite(href: string): string {
    const url = new URL(href);

    url.searchParams.delete(TABLE_PARAM);

    return url.href;
}

/**
 * The dialog is up while the table is being set, and during the game for
 * anyone who still has to pick a seat to drop into.
 */
export function shouldShowLobby(lobby: TableLobbyMessage | null): boolean {
    return lobby !== null && (lobby.phase === "seating" || lobby.yourSeat === null);
}

/** True when the recipient is seated at a game that is under way. */
export function isPlaying(lobby: TableLobbyMessage | null): boolean {
    return lobby !== null && lobby.phase === "playing" && lobby.yourSeat !== null;
}

export function groupTeams(lobby: TableLobbyMessage): readonly LobbyTeam[] {
    return lobby.teamColors.map((team) => ({
        teamId: team.teamId,
        color: team.color,
        seats: lobby.seats.filter((seat) => seat.teamId === team.teamId)
    }));
}

/**
 * A bot's seat is anyone's to take while the table is being set; once the
 * game is under way, only someone without a seat may take one over.
 */
export function canTakeSeat(lobby: TableLobbyMessage, seat: LobbySeat): boolean {
    return !seat.isPlayer && (lobby.phase === "seating" || lobby.yourSeat === null);
}

/** The team the recipient sits on, or null when they have no seat. */
export function getOwnTeamId(lobby: TableLobbyMessage): string | null {
    return lobby.seats.find((seat) => seat.isYou)?.teamId ?? null;
}

/** Colors a team could switch to: its own, and any no other team has. */
export function getFreeColors(lobby: TableLobbyMessage, teamId: string): readonly TeamColor[] {
    const taken = new Set(lobby.teamColors.filter((team) => team.teamId !== teamId).map((team) => team.color));

    return TEAM_COLOR_ORDER.filter((color) => !taken.has(color));
}

function getHolder(seat: LobbySeat): SeatHolder {
    if (!seat.isPlayer) {
        return "bot";
    }

    return seat.away ? "away" : "player";
}

/** Who holds each seat, keyed by the player id the game view uses. */
export function getSeatHolders(lobby: TableLobbyMessage | null): Readonly<Record<string, SeatHolder>> {
    return Object.fromEntries((lobby?.seats ?? []).map((seat) => [seat.playerId, getHolder(seat)]));
}

function classifyChange(before: LobbySeat, after: LobbySeat): SeatChangeKind | null {
    if (before.isPlayer !== after.isPlayer) {
        return after.isPlayer ? "joined" : "left";
    }

    if (!after.isPlayer) {
        return null;
    }

    // Someone else got there between two updates: still an arrival.
    if (before.name !== after.name) {
        return "joined";
    }

    if (before.away === after.away) {
        return null;
    }

    return after.away ? "away" : "returned";
}

/**
 * What changed at a game in progress between two seating updates. Only a
 * player already seated at that game is told, and never about their own seat:
 * sitting down, or the table being set, is news to nobody watching the felt.
 */
export function diffSeats(previous: TableLobbyMessage | null, next: TableLobbyMessage): readonly SeatChange[] {
    if (!isPlaying(previous) || !isPlaying(next) || previous?.playerCount !== next.playerCount) {
        return [];
    }

    return next.seats.flatMap((after) => {
        const before = previous.seats.find((seat) => seat.seatIndex === after.seatIndex);
        const kind = before === undefined || after.isYou ? null : classifyChange(before, after);

        if (before === undefined || kind === null) {
            return [];
        }

        return [{ playerId: after.playerId, kind, name: kind === "left" ? before.name : after.name }];
    });
}

/** "PurplePanda#2953" without its number, which is how a seat is spoken of. */
function shortName(name: string): string {
    const hash = name.lastIndexOf("#");

    return hash === -1 ? name : name.slice(0, hash);
}

export function describeSeatChange(change: SeatChange): SeatChangeText {
    const name = shortName(change.name);

    switch (change.kind) {
        case "joined": {
            return { callout: "Joined the game", announcement: `${name} joined the game, taking over a bot's seat.` };
        }

        case "left": {
            return { callout: `${name} left · a bot took over`, announcement: `${name} left the game. A bot is playing their seat.` };
        }

        case "away": {
            return {
                callout: "Away · a bot is covering",
                announcement: `${name} lost their connection. A bot is playing for them until they are back.`
            };
        }

        case "returned": {
            return { callout: "Back at the table", announcement: `${name} is back at the table.` };
        }
    }
}
